import React from 'react';
import type { GapRow } from '../../data/strategy';
type GapChartProps = {
  rows: GapRow[];
};
const TICKS = [1, 2, 3, 4, 5];
const GAP_COLOR = '#7C3AED';
const FLAT_COLOR = '#CBD5E1';
function toPercent(value: number): number {
  return (value - 1) / 4 * 100;
}
export function GapChart({
  rows
}: GapChartProps) {
  return <div className="flex h-full min-h-0 w-full flex-col" data-id="element-11" data-mp="3bv67awm1y0r1">
      <div className="flex min-h-0 flex-1 flex-col" data-id="element-12" data-mp="3pvgxryjg9dbo">
        {rows.map((row, index) => {
        const delta = row.believed - row.perceived;
        const isGap = delta >= 0.5;
        return <div key={row.label} className="flex min-h-0 flex-1 items-stretch" data-id="element-13" data-mp="3gnrg8pvdgwv" data-mpi={index}>
              <div className="flex w-[22%] flex-col justify-center pr-[2%]" data-id="element-14" data-mp="3umfegz8codfg">
                <span className="text-[clamp(9px,1vw,17px)] font-semibold uppercase tracking-[0.06em] text-ink" data-id="element-15" data-mp="3uoifsfeydszh">
                  <span className="text-ink-mute" data-id="element-16" data-mp="dbxriqwpje80">{index + 1}. </span>
                  {row.label}
                </span>
                <span className="text-[clamp(8px,0.85vw,13px)] font-medium text-ink-mute" data-id="element-17" data-mp="1m66wc01xx6se">
                  {row.importance}% of decisions
                </span>
              </div>

              <div className={['relative flex-1', index % 2 === 0 ? 'bg-paper-tint' : 'bg-transparent'].join(' ')} data-id="element-18" data-mp="1v6lggw364fub">
                {TICKS.map(tick => <span key={tick} aria-hidden="true" className="absolute top-0 bottom-0 w-px bg-ink-line" style={{
              left: `${toPercent(tick)}%`
            }} data-id="element-19" data-mp="253q2mvx0kbb4" />)}

                <span aria-hidden="true" className="absolute top-1/2 -translate-y-1/2 rounded-full" style={{
              left: `${toPercent(row.perceived)}%`,
              width: `${toPercent(row.believed) - toPercent(row.perceived)}%`,
              height: 'clamp(4px, 0.5vw, 9px)',
              background: isGap ? GAP_COLOR : FLAT_COLOR
            }} data-id="element-20" data-mp="2ivxymlgh3y8r" />

                <span className="absolute top-1/2 rounded-full border-[2px] border-ink bg-paper-card" style={{
              left: `${toPercent(row.perceived)}%`,
              transform: 'translate(-50%, -50%)',
              width: 'clamp(11px, 1.3vw, 21px)',
              height: 'clamp(11px, 1.3vw, 21px)'
            }} title={`HCP perception ${row.perceived}`} data-id="element-21" data-mp="nqyak3hpxp8w" />
                <span className="absolute top-1/2 rounded-full bg-brand-enstilar" style={{
              left: `${toPercent(row.believed)}%`,
              transform: 'translate(-50%, -50%)',
              width: 'clamp(11px, 1.3vw, 21px)',
              height: 'clamp(11px, 1.3vw, 21px)'
            }} title={`Roche fact base ${row.believed}`} data-id="element-22" data-mp="1lfgsykcu60fy" />
              </div>

              <div className="flex w-[17%] flex-col justify-center pl-[2.5%]" data-id="element-23" data-mp="22qnf3os00z5u">
                <span className="font-display text-[clamp(16px,2.1vw,36px)] leading-none" style={{
              color: isGap ? GAP_COLOR : '#94A3B8'
            }} data-id="element-24" data-mp="1o17oq9wdqvqo">
                  +{delta.toFixed(1)}
                </span>
                <span className="text-[clamp(8px,0.85vw,13px)] font-semibold uppercase tracking-[0.08em] text-ink-mute" data-id="element-25" data-mp="1ezq9suupl03k">
                  {row.verdict}
                </span>
              </div>
            </div>;
      })}
      </div>

      <div className="flex shrink-0" data-id="element-26" data-mp="1a1giwwg0olnr">
        <div className="w-[22%]" data-id="element-27" data-mp="1a2hrmu9k8w2b" />
        <div className="relative flex-1 border-t border-ink/30" data-id="element-28" data-mp="1a3j515e1tun7">
          {TICKS.map(tick => <span key={tick} className="absolute top-[0.4vh] text-[clamp(7px,0.8vw,13px)] font-medium text-ink-mute" style={{
          left: `${toPercent(tick)}%`,
          transform: 'translateX(-50%)'
        }} data-id="element-29" data-mp="2zea6376azpbw">
              {tick}
            </span>)}
        </div>
        <div className="w-[17%]" data-id="element-30" data-mp="vbqok2fxgobz" />
      </div>

      <ul className="mt-[1.6vh] flex shrink-0 items-center gap-[2.4vw] pl-[22%]" data-id="element-31" data-mp="vemofwbc5n32">
        <li className="flex items-center gap-[0.6vw] text-[clamp(8px,0.9vw,14px)] font-medium text-ink-soft" data-id="element-32" data-mp="qd73e495rgko">
          <span aria-hidden="true" className="rounded-full border-[2px] border-ink bg-paper-card" style={{
          width: 'clamp(9px,1vw,16px)',
          height: 'clamp(9px,1vw,16px)'
        }} data-id="element-33" data-mp="lggnpi19mmzs" />
          What HCPs believe today
        </li>
        <li className="flex items-center gap-[0.6vw] text-[clamp(8px,0.9vw,14px)] font-medium text-ink-soft" data-id="element-34" data-mp="7o0kag4gcmyc">
          <span aria-hidden="true" className="rounded-full bg-brand-enstilar" style={{
          width: 'clamp(9px,1vw,16px)',
          height: 'clamp(9px,1vw,16px)'
        }} data-id="element-35" data-mp="2ncs3r4hm02j" />
          What Roche can defend
        </li>
        <li className="flex items-center gap-[0.6vw] text-[clamp(8px,0.9vw,14px)] font-medium text-gapAccent" data-id="element-36" data-mp="3opbqob1mpyc2">
          <span aria-hidden="true" className="rounded-full bg-gapAccent" style={{
          width: 'clamp(16px,2vw,32px)',
          height: 'clamp(4px,0.5vw,9px)'
        }} data-id="element-37" data-mp="3tooeay0br9fp" />
          The gap we have to close
        </li>
      </ul>
    </div>;
}